import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  faBars,
  faCaretCircleLeft,
  faPauseCircle,
  faTimesCircle,
  faUserAlt
} from '@fortawesome/pro-solid-svg-icons';
import { Container } from '../../../grid';
import Icon from '../../atoms/icon';
import Tag from '../../atoms/tag';
import Meta from '../../atoms/meta';
import Text from '../../atoms/text';
import { Tabs, Tab } from '../../molecules/tab';
import Button from '../../molecules/button';
import ErrorBoundary from '../../molecules/error-boundary';
import PageHeader from '../../organisms/page-header';
import StatusIndicator from '../../organisms/status/status-indicator';
import PatientEvents from '../../organisms/patient/events';
import StatusBadge from '../../organisms/status/status-badge';
import { RoomWrapper } from '../../organisms/room';
import { ArchivePatientAction } from '../../organisms/action/archive';
import { PushMessageAction } from '../../organisms/action/push';
import { PausePatientAction } from '../../organisms/action/pause';

import { useApps } from '../../../contexts/apps';
import { useTriage } from '../../../contexts/triage';
import {
  GetSecondaryCategoryForApp,
  GetTriagePatientData
} from '../../../contexts/triage/utils';
import { AppKey } from '../../../libs/apps';
import { TriagedApp, TriagedPatient } from '../../../contexts/triage/types';
import { useTriagePatient } from '../../../contexts/triage/patient';
import { useMetaData } from '../../../contexts/metadata';
import { IPatient } from '../../../libs/patient';
import { PatientTabIdType } from '../../../libs/patient';
import { useGetRoutes } from '../../../libs/route';
import { IsTriageAppEnded } from '../../../libs/schedule';
import { IsAlertResolved, IsAlertSnoozed } from '../../../libs/alert';
import type { IAlert } from '../../../libs/alert';

import LoadingPage from '../loading';
import PatientNotFoundPage from './not_found';
import PatientHeader from './header';
import PatientProgram from './program';
import PatientInfo from './info';
import PatientLogs from '../time-tracked/patient';
import { IsInIframe } from '../../../libs/browser';
import QualitySurvey, { surveyDefaultData } from '../survey/quality-survey';
import { ICustomProgram, useGetCustomPrograms } from '../../../libs/composer';
import TriageRefreshBanner from '../../organisms/banner/triage-refresh';
import { getPausedApps, isFullPause } from '../../../libs/tasks';

/**
  - Patient Header
  - Side Navigation
    - Programs
    - Past Programs (if present)
    - Other
    - Event Notes
    - Patient Information
    - Pause
    - Archive
  - Program Tab
    - Program Navigation
      - Data
      - Event Notes
  - Sections
    - Alerts
    - Summary
    - Graph
    - All Responses
*/

interface PatientPageProps {
  externalId?: number;
  externalTabId?: PatientTabIdType;
  close?: () => void;
}

const getTags = (patient: IPatient) => {
  if (!patient) {
    return [];
  }
  if (!patient.tags || !patient.tags.length) {
    return [];
  }
  return patient.tags.split(',').map((tag, index) => {
    return (
      <Tag
        key={index}
        className="mt-0-important mb-0-important mr-2"
        text={tag}
      />
    );
  });
};

interface PatientProgramTab {
  tabId: string;
  displayKey: string;
  data: TriagedApp;
  alertAppKeys: string[];
  eventAppKeys: string[];
  routeKeys: string[];
}

/**
Logs tab merge diagnostics when debug mode is enabled.

Enable by setting `window.__CS_DEBUG_PROGRAM_MERGE = true` in devtools.

@param payload diagnostic object
 */
const debugProgramMerge = (payload: Record<string, unknown>) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!(window as any).__CS_DEBUG_PROGRAM_MERGE) {
    return;
  }
  console.info('[patient-tab-merge]', payload);
};

/**
Build merged patient program tabs so schema programs mapped from hardcoded apps
show up as a single program experience.

@param appEntries patient app entries from triage payload
@returns merged tab definitions
 */
const buildPatientProgramTabs = (
  appEntries: TriagedPatient['apps'],
  customProgramsByApp: Map<string, ICustomProgram>
): PatientProgramTab[] => {
  const resolveProgram = (
    segment?: TriagedApp,
    fallbackAppKey?: string
  ): ICustomProgram | undefined => {
    if (!segment) {
      return undefined;
    }
    const triageProgram = segment.customProgram;
    const lookupAppKey = triageProgram?.app || fallbackAppKey;
    const accountProgram = lookupAppKey
      ? customProgramsByApp.get(lookupAppKey)
      : null;
    if (!triageProgram) {
      return accountProgram || undefined;
    }
    if (triageProgram.id != null) {
      return triageProgram;
    }
    if (!accountProgram) {
      return triageProgram;
    }
    return {
      ...triageProgram,
      id: accountProgram.id,
      accountId: accountProgram.accountId,
      createdAt: accountProgram.createdAt,
      updatedAt: accountProgram.updatedAt,
      deletedAt: accountProgram.deletedAt
    };
  };
  const tabs: PatientProgramTab[] = [];
  const consumed = new Set<string>();
  const customByLegacy = new Map<string, string>();
  const legacyByCustom = new Map<string, string>();
  const appKeys = Object.keys(appEntries || {});
  // Strong fallback: if account metadata declares a mapping and both keys are
  // present in triage apps, treat them as a merge pair even when triage
  // customProgram metadata is stale.
  for (const accountProgram of customProgramsByApp.values()) {
    if (!accountProgram?.legacyAppKey || !accountProgram?.app) {
      continue;
    }
    if (
      appKeys.includes(accountProgram.legacyAppKey) &&
      appKeys.includes(accountProgram.app)
    ) {
      customByLegacy.set(accountProgram.legacyAppKey, accountProgram.app);
    }
  }
  for (const key of Object.keys(appEntries || {})) {
    const segment = appEntries[key] as TriagedApp;
    const legacyKey =
      segment?.customProgram?.legacyAppKey ||
      resolveProgram(segment, key)?.legacyAppKey;
    if (legacyKey != null && legacyKey.length > 0) {
      customByLegacy.set(legacyKey, key);
      legacyByCustom.set(key, legacyKey);
    }
  }
  debugProgramMerge({
    phase: 'mapping',
    appKeys,
    customByLegacy: Object.fromEntries(customByLegacy),
    accountCustomProgramApps: Array.from(customProgramsByApp.keys())
  });
  for (const key of Object.keys(appEntries || {})) {
    if (key === 'composer' || consumed.has(key)) {
      continue;
    }
    const linkedLegacyKey = legacyByCustom.get(key);
    if (
      linkedLegacyKey != null &&
      linkedLegacyKey !== key &&
      appEntries?.[linkedLegacyKey]
    ) {
      // Defer mapped custom app keys so the linked legacy key builds
      // the single merged tab regardless of object iteration order.
      continue;
    }
    const segment = appEntries[key] as TriagedApp;
    if (segment == null) {
      continue;
    }
    const mappedCustomKey = customByLegacy.get(key);
    if (mappedCustomKey != null && mappedCustomKey !== key) {
      const customSegment = appEntries[mappedCustomKey] as TriagedApp;
      if (customSegment != null) {
        const resolvedProgram = resolveProgram(customSegment, mappedCustomKey);
        const mergedAlert = {
          total:
            (customSegment.alert?.total || 0) + (segment.alert?.total || 0),
          unread:
            (customSegment.alert?.unread || 0) + (segment.alert?.unread || 0),
          active:
            (customSegment.alert?.active || 0) + (segment.alert?.active || 0)
        };
        tabs.push({
          tabId: key,
          displayKey: key,
          data: {
            ...customSegment,
            customProgram: resolvedProgram,
            category: Math.min(customSegment.category, segment.category),
            alert: mergedAlert
          },
          alertAppKeys: [key, mappedCustomKey],
          eventAppKeys: [key, mappedCustomKey],
          routeKeys: [mappedCustomKey, key]
        });
        consumed.add(key);
        consumed.add(mappedCustomKey);
        continue;
      }
    }
    tabs.push({
      tabId: key,
      displayKey: key,
      data: {
        ...segment,
        customProgram: resolveProgram(segment, key)
      },
      alertAppKeys: [key],
      eventAppKeys: [key],
      routeKeys: [key]
    });
    consumed.add(key);
  }
  debugProgramMerge({
    phase: 'tabs',
    tabs: tabs.map((tab) => ({
      tabId: tab.tabId,
      routeKeys: tab.routeKeys,
      alertAppKeys: tab.alertAppKeys,
      eventAppKeys: tab.eventAppKeys,
      customApp: tab.data?.customProgram?.app,
      legacyAppKey: tab.data?.customProgram?.legacyAppKey
    }))
  });
  return tabs;
};

const getTabId = (
  data: TriagedPatient,
  externalTabId: PatientTabIdType,
  urlTabId: string
): string => {
  if (!data) {
    return null;
  }
  // We have an externalTabId, meaning we're accessing
  // the PatientPage via the patient details dialog from a URL like
  // /t/not-engaged/?patientId=3&patientTab=epxdiabetes
  if (externalTabId) {
    return externalTabId.toString();
  }
  // We are accessing PatientPage via a URL like
  // /patient/5/epxdiabetes where 3 = tabId
  if (urlTabId) {
    return urlTabId;
  }
  // We are accessing PatientPage via a URL like
  // patient/5/ OR via patient details dialog from a URL like
  // /t/not-engaged/?patientId=3
  const alerts: IAlert[] = Array.isArray(data.alerts) ? data.alerts : [];
  if (alerts.length) {
    const openAlerts: IAlert[] = alerts
      .filter((alert) => !IsAlertResolved(alert) && !IsAlertSnoozed(alert))
      .sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });
    if (openAlerts.length > 0) {
      const mostRecent = openAlerts.find((alert) => !!data.apps?.[alert.app]);
      if (mostRecent?.app) {
        return mostRecent.app;
      }
    }
  }
  return Object.keys(data.apps)[0];
};

const createNewURLPath = (
  location: any,
  externalId: number,
  patientId: number,
  newTabId: string
) => {
  // External id indicates PatientPage is loading
  // inside the modal, so we have to update the URL
  // in a different way
  if (externalId) {
    if (!location.search.includes('patientTab')) {
      return `${location.pathname}${location.search}&patientTab=${newTabId}${location.hash}`;
    }
    // Updating an existing patientTab
    const search = location.search
      .split('&')
      .map((part) => {
        if (part.includes('patientTab')) {
          return `patientTab=${newTabId}`;
        }
        return part;
      })
      .join('&');
    return `${location.pathname}${search}${location.hash}`;
  } else {
    return `/patient/${patientId}/${newTabId}/`;
  }
};

const shouldShowPushMessageOption = (
  isPushMessageEnabled: boolean,
  isLightbeam: boolean
) => {
  if (!isPushMessageEnabled) {
    return false;
  }
  if (isLightbeam && IsInIframe()) {
    return false;
  }
  return true;
};

const PatientPage: React.FC<PatientPageProps> = ({
  externalId,
  externalTabId,
  close
}): JSX.Element => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const tabWidth = isSidebarExpanded ? '250px' : '100%';
  const { terms } = useMetaData();
  const { isReadOnly, isLightbeam, account } = useMetaData();
  const { apps: appConfigs } = useApps().data || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { customPrograms: accountCustomPrograms } = useGetCustomPrograms({
    accountId: account?.id || 0,
    page: 0,
    limit: 500
  });
  // Fetch the triage entry based on patient ID
  const params = useParams();
  const _patientId = params.id;
  const _urlTabId = params.tab;
  const patientId = externalId ? externalId : parseInt(_patientId, 10);
  // Validate patientId if not using externalId
  if (
    !externalId &&
    (isNaN(patientId) || patientId === null || patientId === undefined)
  ) {
    return <PatientNotFoundPage />;
  }
  const { isLoading, triageInProgress } = useTriage();
  const {
    patientData: data,
    updatePatientId,
    isPatientLoading
  } = useTriagePatient();
  const [directPatientData, setDirectPatientData] =
    useState<TriagedPatient>(null);
  const [isDirectPatientLoading, setIsDirectPatientLoading] =
    useState<boolean>(false);
  useEffect(() => {
    let isCancelled = false;
    if (updatePatientId) {
      updatePatientId(patientId);
      return;
    }
    if (patientId != null && patientId > 0) {
      setIsDirectPatientLoading(true);
      setDirectPatientData(null);
      GetTriagePatientData(patientId, true)
        .then((res) => {
          if (!isCancelled) {
            setDirectPatientData(res);
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setIsDirectPatientLoading(false);
          }
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [patientId]);
  const resolvedData = data || directPatientData;
  const resolvedIsPatientLoading = isPatientLoading || isDirectPatientLoading;
  // Grab the schema-based programs if needed
  const customPrograms = [];
  if (resolvedData) {
    Object.values(resolvedData?.apps).map((app) => {
      const program: ICustomProgram = app.customProgram;
      if (!program) {
        return;
      }
      customPrograms.push({
        id: program.id,
        app: program.app,
        name: program.name,
        phone: program.phone
      });
    });
  }
  // Fetch the routes
  const { routes } = useGetRoutes({
    keys: resolvedData?.apps ? Object.keys(resolvedData.apps) : [],
    patientId: resolvedData?.patient?.id,
    customPrograms: customPrograms.length > 0 ? customPrograms : null
  });
  // Set the tab if necessary
  const customProgramsByApp = new Map<string, ICustomProgram>();
  for (const program of accountCustomPrograms || []) {
    if (program?.app) {
      customProgramsByApp.set(program.app, program);
    }
  }
  const programTabs = buildPatientProgramTabs(
    resolvedData?.apps,
    customProgramsByApp
  );
  const sourceTabKeyToDisplayKey: Record<string, string> = {};
  for (const programTab of programTabs) {
    for (const key of programTab.eventAppKeys) {
      sourceTabKeyToDisplayKey[key] = programTab.tabId;
    }
  }
  const _rawTabId = getTabId(resolvedData, externalTabId, _urlTabId);
  const _tabId = sourceTabKeyToDisplayKey[_rawTabId] || _rawTabId;
  const [tabId, setTabId] = useState<string>(_tabId);
  useEffect(() => {
    setTabId(_tabId);
  }, [_tabId]);
  // Loading & not found
  const isInDialog = close != null;
  if (
    (isLoading || resolvedIsPatientLoading) &&
    (!resolvedData ||
      !appConfigs ||
      (resolvedData && resolvedData.patient.id !== patientId))
  ) {
    return <LoadingPage pastDelay isSubComponent={isInDialog} />;
  }
  if (!resolvedData && !isLoading && !resolvedIsPatientLoading) {
    return <PatientNotFoundPage />;
  }
  const { apps, patient } = resolvedData;
  // Display the list of tags for the patient
  const tags = getTags(patient);
  // Determine which programs are paused
  const pausedAppKeys = patient.tasks ? getPausedApps(patient.tasks) : [];
  const isFullPatientPause = patient.tasks ? isFullPause(patient.tasks) : true;
  const activePrograms = [];
  const endedPrograms = [];
  const otherPrograms = [];
  for (const programTab of programTabs) {
    const key = programTab.displayKey;
    const appEntry = programTab.data;
    const appCategory = GetSecondaryCategoryForApp(appEntry, appEntry.category);
    let tabPadding = 'pl-3';
    if (appEntry.alert.active || appCategory !== appEntry.category) {
      tabPadding = 'pl-0';
    }
    const tab = (
      <Tab
        key={key}
        id={key}
        title={
          isSidebarExpanded ? (
            <span className={tabPadding}>
              {appCategory !== appEntry.category ? (
                <StatusBadge category={appCategory} />
              ) : null}
              {appEntry.alert.active ? (
                <StatusBadge
                  alert={{ total: appEntry.alert.active as number }}
                />
              ) : null}
              {patient.isSuspended &&
              (isFullPatientPause || pausedAppKeys.includes(key)) ? (
                <Icon
                  className="mr-1 text-intent-warning text-small"
                  icon={faPauseCircle}
                  title="Program Paused"
                />
              ) : null}
              <StatusIndicator
                category={appEntry.category}
                minimal={true}
                title={false}
              />
              {appConfigs[key]?.name || appEntry.customProgram?.name || key}
            </span>
          ) : null
        }
        className="pa-0-important"
        style={{
          width: tabWidth
        }}
        panelClassName="vtable-parent"
        panel={
          <PatientProgram
            appKey={key as AppKey}
            data={appEntry}
            patientId={patient.id}
            alertAppKeys={programTab.alertAppKeys}
            eventAppKeys={programTab.eventAppKeys}
            isSidebarExpanded={isSidebarExpanded}
            setIsSidebarExpanded={setIsSidebarExpanded}
            chartReflowTrigger={isSidebarExpanded}
            routes={
              routes
                ? routes[programTab.routeKeys[0]] ||
                  routes[programTab.routeKeys[1]] ||
                  routes[key] ||
                  null
                : null
            }
          />
        }
      />
    );
    if (IsTriageAppEnded(appEntry)) {
      endedPrograms.push(tab);
    } else {
      activePrograms.push(tab);
    }
  }
  const qualitySurveyTab = (
    <Tab
      key={surveyDefaultData.appKey}
      id={surveyDefaultData.appKey}
      title={
        isSidebarExpanded ? (
          <span className={'pl-3'}>{surveyDefaultData.name}</span>
        ) : null
      }
      className="pa-0-important"
      style={{
        width: tabWidth
      }}
      panelClassName="vtable-parent"
      panel={
        <QualitySurvey
          patientId={patient.id}
          isSidebarExpanded={isSidebarExpanded}
          setIsSidebarExpanded={setIsSidebarExpanded}
        />
      }
    />
  );
  otherPrograms.push(qualitySurveyTab);
  const onTabChange = (tabId: string): void => {
    setTabId(tabId);
    const urlPath = createNewURLPath(location, externalId, patientId, tabId);
    navigate(urlPath, { replace: true });
  };
  return (
    <ErrorBoundary>
      <Meta title={`CSID: ${patient.id}`} />
      <RoomWrapper
        patientId={patientId}
        borderWidth="4"
        includeMembers
        joinRoom
      >
        <PageHeader className="bp5-elevation-1">
          <Container fluid style={{ alignItems: 'center' }}>
            <h1 className="font-serif text-serif-lg inline-block align-middle">
              <Icon className="mr-2" icon={faUserAlt} />
              {terms.patient.capitalized} Details
            </h1>
            <span className="ml-4 text-sans-sm font-bold">CSID</span>{' '}
            <Tag
              className="ma-0-important"
              intent="primary"
              text={String(patient.id)}
            />
            {patient.mrn ? (
              <React.Fragment>
                <span className="ml-4 text-sans-sm font-bold">
                  {terms.mrn.capitalized}
                </span>{' '}
                {patient.mrn}
              </React.Fragment>
            ) : null}
            {tags?.length ? (
              <React.Fragment>
                <span className="ml-4 text-sans-sm font-bold">TAGS</span> {tags}
              </React.Fragment>
            ) : null}
            {close ? (
              <Button
                style={{
                  position: 'absolute',
                  right: 0
                }}
                icon={<Icon icon={faTimesCircle} />}
                variant="minimal"
                onClick={close}
              />
            ) : null}
          </Container>
        </PageHeader>
        <TriageRefreshBanner isVisible={triageInProgress} className="mb-0" />
        <PatientHeader />
        <Tabs
          className="bp5-sidetabs"
          id="patient"
          selectedTabId={tabId}
          onChange={onTabChange}
          animate={false}
          vertical
          renderActiveTabPanelOnly
        >
          {isSidebarExpanded ? null : (
            <Button
              variant="minimal"
              onClick={() => setIsSidebarExpanded(true)}
            >
              <Icon icon={faBars} />
            </Button>
          )}
          {activePrograms}
          {endedPrograms.length > 0 ? (
            <div
              className="bp5-tab-divider uppercase"
              style={{
                display: isSidebarExpanded ? 'block' : 'none'
              }}
            >
              Past Programs
            </div>
          ) : null}
          {endedPrograms}
          <div
            className="bp5-tab-divider uppercase"
            style={{
              display: isSidebarExpanded ? 'block' : 'none'
            }}
          >
            Other
          </div>
          {otherPrograms}
          <div
            className="bp5-tab-divider uppercase"
            style={{
              display: isSidebarExpanded ? 'block' : 'none'
            }}
          >
            Information
          </div>
          <Tab
            id="information"
            title={
              isSidebarExpanded
                ? `${terms.patient.capitalized} Information`
                : null
            }
            style={{
              width: tabWidth
            }}
            panel={<PatientInfo />}
          />
          <Tab
            id="events"
            title={isSidebarExpanded ? 'Event Notes' : null}
            style={{
              width: tabWidth
            }}
            panel={
              <Container>
                <h3>Event Notes</h3>
                <div className="pb-8">
                  <PatientEvents patientId={patient.id} />
                </div>
              </Container>
            }
          />
          {account?.isTimeTrackedEnabled ? (
            <Tab
              id="time-tracked"
              title={isSidebarExpanded ? 'Time Tracked' : null}
              style={{
                width: tabWidth
              }}
              panel={
                <Container>
                  <h3>Time Tracked</h3>
                  <div className="pb-8">
                    <PatientLogs patientId={`${patient.id}`} />
                  </div>
                </Container>
              }
            />
          ) : null}
          <div
            className="bp5-tab-divider uppercase"
            style={{
              display: isSidebarExpanded ? 'block' : 'none'
            }}
          >
            Additional Actions
          </div>
          <div
            className="w-full"
            style={{
              display: isSidebarExpanded ? 'block' : 'none'
            }}
          >
            {!isReadOnly && (
              <>
                <PausePatientAction patient={patient} apps={apps} fill />
                <ArchivePatientAction patient={patient} fill />
                {shouldShowPushMessageOption(
                  account?.isPushMessageEnabled,
                  isLightbeam
                ) ? (
                  <PushMessageAction patient={patient} templateType="pm" />
                ) : null}
              </>
            )}
            <Button
              className="justify-left"
              onClick={() => setIsSidebarExpanded(false)}
              variant="minimal"
              fill
            >
              <Icon icon={faCaretCircleLeft} /> <Text dotted>Collapse</Text>
            </Button>
          </div>
        </Tabs>
      </RoomWrapper>
    </ErrorBoundary>
  );
};

export default PatientPage;
